/**
 * fetchWithRetry — fetch that retries transient failures (network error or 5xx),
 * but NOT definitive 4xx. A momentary backend blip (e.g. the asset-manager
 * returning 502 while it restarts under bake load) must not permanently mark a
 * baked artifact as "missing" — that left lighting/reflections/navmesh showing
 * "not ready" with black spheres until something forced a re-fetch (ENG-592).
 *
 * 404 is treated as definitive (the artifact genuinely isn't baked) and returns
 * immediately. Returns the final Response (caller checks `.ok`), or throws if
 * every attempt errored at the network layer.
 */
export async function fetchWithRetry(url, { retries = 2, backoffMs = 600, cache } = {}) {
  const init = cache ? { cache } : undefined
  let lastErr = null
  for (let attempt = 0; attempt <= retries; attempt++) {
    let res = null
    try {
      res = await fetch(url, init)
    } catch (e) {
      lastErr = e
      if (attempt === retries) throw e
      await sleep(backoffMs * (attempt + 1))
      continue
    }
    // Success, or a definitive client error (404 = not baked) — don't retry.
    if (res.ok || (res.status >= 400 && res.status < 500)) return res
    // Transient (5xx): retry unless we're out of attempts.
    if (attempt === retries) return res
    await sleep(backoffMs * (attempt + 1))
  }
  if (lastErr) throw lastErr
}

function sleep(ms) { return new Promise(r => setTimeout(r, ms)) }
